import { useLocation, useNavigate } from "react-router-dom";
import { useT } from "../i18n/LanguageProvider";
import { C, font } from "../styles/tokens";

/**
 * MobileBottomNav -- fixed bottom tab bar for Professional role on mobile.
 *
 * Matches prototype exactly:
 *   Profile | Verifications | Add | Share
 *   Active tab shows blue icon + blue top border rule
 *
 * Only renders on mobile (< 700px). The AppShell already hides it on desktop.
 * Body bottom padding is added by AppShell when this renders so content
 * isn't hidden behind the bar.
 */
export function MobileBottomNav() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();

  const items = [
    {
      label: "Profile",
      to: "/profile",
      icon: ProfileIcon,
    },
    {
      label: "Verify",
      to: "/verifications",
      icon: ShieldIcon,
    },
    {
      label: "Add",
      to: "/credentials/new/education",
      icon: PlusIcon,
      isAction: true,
    },
    {
      label: "Payments",
      to: "/billing",
      icon: InboxIcon,
    },
  ];

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: 56,
        background: C.bg,
        borderTop: `1px solid ${C.divider}`,
        display: "flex",
        zIndex: 100,
        // Safe area for iPhone home indicator
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {items.map((item) => {
        const active =
          location.pathname === item.to ||
          (item.to === "/profile" &&
            location.pathname.startsWith("/credentials"));
        const Icon = item.icon;

        if (item.isAction) {
          return (
            <button
              key={item.to}
              onClick={() => navigate(item.to)}
              style={{
                flex: 1,
                border: 0,
                background: "transparent",
                cursor: "pointer",
                fontFamily: font,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: C.blue,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon size={18} color="white" />
              </div>
            </button>
          );
        }

        return (
          <button
            key={item.to}
            onClick={() => navigate(item.to)}
            style={{
              flex: 1,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              fontFamily: font,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              color: active ? C.blue : C.textMed,
              borderTop: active
                ? `2px solid ${C.blue}`
                : "2px solid transparent",
              marginTop: -1,
            }}
          >
            <Icon size={22} color={active ? C.blue : C.textMed} />
            <span
              style={{
                fontSize: 10,
                fontWeight: active ? 700 : 500,
                color: active ? C.blue : C.textMed,
                lineHeight: 1,
              }}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---- Icons ----

function ProfileIcon({ size, color }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
    </svg>
  );
}

function ShieldIcon({ size, color }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function PlusIcon({ size, color }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function InboxIcon({ size, color }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}
